// frontend/src/lib/p2p/errors.ts
import { decodeErrorResult } from 'viem';
import { P2P_ESCROW_ABI } from './abi';

const HUMAN_MESSAGES: Record<string, string> = {
  AmountTooSmall: 'Monto menor al mínimo permitido',
  PremiumOutOfRange: 'Prima fuera del rango aceptado',
  InvalidExpiration: 'Expiración inválida',
  ExpirationOverflow: 'Expiración inválida (overflow)',
  OrderNotFound: 'Orden no encontrada',
  NotOrderMaker: 'No eres el creador de esta orden',
  InvalidStatus: 'Esta orden ya no se puede modificar',
  AlreadyExpired: 'La orden ya expiró',
  NotExpired: 'La orden aún no expira',
  RateLimitExceeded: 'Volumen 24h excedido en el contrato',
  EmptyBatch: 'Lote vacío',
  EnforcedPause: 'El intercambio está pausado',
  ExpectedPause: 'El contrato debe estar pausado para esta acción',
  NotRelayer: 'No autorizado (relayer)',
  InvalidAddress: 'Dirección inválida',
  GasFeeFlatTooHigh: 'gasFeeFlat por encima del hard-cap (5 tokens)',
  TakeMaxTtlInvalid: 'takeMaxTtlSeconds inválido (0 o > 600s)',
  InvalidTakeTarget: 'Target maker order requerido para take',
  FeeExceedsFill: 'Monto del leg menor o igual al fee total — no rentable',
  FeeBpsTooHigh: 'platformFeeBps por encima del hard-cap (500 = 5%)',
  MaxVolumePer24hZero: 'maxVolumePer24h no puede ser cero',
  InsufficientFees: 'No hay fees suficientes acumulados para retirar esa cantidad',
  MinOrderAmountZero: 'minOrderAmount no puede ser cero',
  FeeConfigInconsistent: 'Config inconsistente: una orden mínima no cubriría el fee total',
};

/**
 * Try to decode a viem `BaseError`'s revert data into a known P2PEscrow
 * custom error. Returns a human-readable string. Falls back to the
 * original message when nothing matches.
 */
export function humanizeP2PError(err: unknown): string {
  if (!err) return 'Error desconocido';
  const e = err as { cause?: any; shortMessage?: string; message?: string };

  // User rejected — short-circuit before any decoding
  const msg = e.shortMessage ?? e.message ?? '';
  if (/user rejected|rejected the request/i.test(msg)) {
    return 'Operación cancelada';
  }

  // WalletConnect/AppKit collapses the on-chain revert into a generic
  // "Request expired" when the wallet doesn't acknowledge in time. This
  // typically masks AmountTooSmall, insufficient gas estimate, or a slow
  // mobile wallet — guide the user instead of leaking the raw RPC error.
  const fullMsg = `${e.shortMessage ?? ''} ${e.message ?? ''} ${(e.cause as { message?: string } | undefined)?.message ?? ''}`;
  if (/request expired/i.test(fullMsg)) {
    return 'El wallet tardó en responder o la simulación falló. Verifica el monto mínimo y reintenta firmando rápido.';
  }

  // Walk the cause chain looking for raw revert data
  let cur: any = err;
  while (cur) {
    const data = cur.data ?? cur.cause?.data;
    if (typeof data === 'string' && data.startsWith('0x')) {
      try {
        const decoded = decodeErrorResult({
          abi: P2P_ESCROW_ABI as any,
          data: data as `0x${string}`,
        });
        const friendly = HUMAN_MESSAGES[decoded.errorName];
        if (friendly) return friendly;
        return `Tx revertida: ${decoded.errorName}`;
      } catch {
        // not one of our errors — fall through
      }
    }
    cur = cur.cause;
  }

  // Fallback to viem's short message
  return e.shortMessage ?? e.message ?? 'Error al ejecutar la transacción';
}
